
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Users, Star, TrendingUp, Target, Award, MessageCircle, BarChart, Heart } from "lucide-react";

const InfluencerMarketingPage = () => {
  useEffect(() => {
    document.title = "Influencer Marketing - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Influencer Partnerships",
      description: "Connect with the right influencers who align with your brand values and target audience"
    },
    {
      icon: <Target className="w-8 h-8 text-blue-600" />,
      title: "Campaign Strategy",
      description: "Develop comprehensive influencer marketing strategies that drive real results"
    },
    {
      icon: <Star className="w-8 h-8 text-blue-600" />,
      title: "Content Creation",
      description: "Collaborate with influencers to create authentic, engaging content for your brand"
    },
    {
      icon: <BarChart className="w-8 h-8 text-blue-600" />,
      title: "Performance Tracking",
      description: "Monitor and analyze campaign performance with detailed analytics and insights"
    },
    {
      icon: <MessageCircle className="w-8 h-8 text-blue-600" />,
      title: "Community Management",
      description: "Manage influencer relationships and community engagement for sustained growth"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      title: "Growth Optimization",
      description: "Continuously optimize campaigns for maximum reach, engagement, and conversions"
    }
  ];

  const platforms = [
    { name: "Instagram", icon: "📸" },
    { name: "TikTok", icon: "🎵" },
    { name: "YouTube", icon: "📺" },
    { name: "Facebook", icon: "👥" },
    { name: "Twitter", icon: "🐦" },
    { name: "LinkedIn", icon: "💼" }
  ];

  const influencerTypes = [
    "Micro-Influencers (1K-100K)",
    "Macro-Influencers (100K-1M)",
    "Mega-Influencers (1M+)",
    "Celebrity Endorsements",
    "Industry Experts",
    "Local Influencers",
    "Niche Specialists",
    "Brand Ambassadors"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Professional influencer marketing services in Nepal. Connect with influencers, create campaigns, and grow your brand reach." />
        <meta name="keywords" content="influencer marketing, social media influencers, brand partnerships, campaign management, Nepal" />
        <meta property="og:title" content="Influencer Marketing - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              Influencer Marketing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Amplify your brand reach through strategic influencer partnerships and authentic content creation.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Platforms Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Platforms We Work With</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {platforms.map((platform, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{platform.icon}</div>
                  <h3 className="font-semibold">{platform.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Influencer Types Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Types of Influencers We Connect You With</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {influencerTypes.map((type, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="font-medium">{type}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Campaign Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Campaign Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Research", desc: "Identify and research suitable influencers" },
                { step: "02", title: "Connect", desc: "Reach out and negotiate partnerships" },
                { step: "03", title: "Create", desc: "Collaborate on authentic content creation" },
                { step: "04", title: "Analyze", desc: "Track performance and optimize results" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Amplify Your Brand?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Let's connect you with the perfect influencers to grow your brand.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default InfluencerMarketingPage;
